
import React, { useState } from 'react';
import { Scale, Brain, Heart, ArrowRight, Sparkles, AlertTriangle, CheckCircle } from 'lucide-react';
import { DebateAnalysis } from '../types';
import { generateCrossroadsDebate } from '../services/geminiService';
import { useTheme } from '../contexts/ThemeContext';
import { BaseModal } from './BaseModal';

interface Props {
  onClose: () => void;
}

export const CrossroadsDebater: React.FC<Props> = ({ onClose }) => {
  const [step, setStep] = useState<'input' | 'loading' | 'results'>('input');
  const [optionA, setOptionA] = useState('');
  const [optionB, setOptionB] = useState('');
  const [result, setResult] = useState<DebateAnalysis | null>(null);
  const { theme } = useTheme();

  const handleDebate = async () => {
    if (!optionA.trim() || !optionB.trim()) return;
    
    setStep('loading');
    try {
        const analysis = await generateCrossroadsDebate(optionA, optionB);
        if (analysis) {
            setResult(analysis);
            setStep('results');
        } else {
            setStep('input');
        }
    } catch (e) {
        console.error(e);
        setStep('input');
    }
  };

  return (
    <BaseModal
        onClose={onClose}
        title="The Crossroads Debater"
        icon={<Scale size={16} />}
        headerColor={theme.colors.features.debater}
        maxWidth="max-w-4xl"
    >
            {step === 'input' && (
                <div className="flex flex-col items-center justify-center h-full max-w-lg mx-auto py-12">
                    <h3 className={`text-2xl font-bold mb-2 text-center ${theme.colors.text.main}`}>What hard choice are you facing?</h3>
                    <p className={`mb-8 text-center ${theme.colors.text.secondary}`}>Kai will simulate a debate between your pragmatic side and your visionary side.</p>
                    
                    <div className="w-full space-y-6">
                        <div className="relative">
                            <label className={`block text-xs font-bold uppercase mb-1 ml-1 ${theme.colors.text.secondary}`}>Option A (The Safe Bet?)</label>
                            <input 
                                type="text" 
                                value={optionA}
                                onChange={(e) => setOptionA(e.target.value)}
                                placeholder="e.g. Stay in my corporate job"
                                className={`w-full p-4 rounded-xl focus:outline-none focus:ring-2 transition-all border ${theme.colors.input.bg} ${theme.colors.input.border} ${theme.colors.input.text} ${theme.colors.input.focusRing}`}
                            />
                        </div>
                        
                        <div className={`flex items-center justify-center font-bold text-sm ${theme.colors.text.secondary}`}>VS</div>

                        <div className="relative">
                            <label className={`block text-xs font-bold uppercase mb-1 ml-1 ${theme.colors.text.secondary}`}>Option B (The Wild Card?)</label>
                            <input 
                                type="text" 
                                value={optionB}
                                onChange={(e) => setOptionB(e.target.value)}
                                placeholder="e.g. Start a coffee shop"
                                className={`w-full p-4 rounded-xl focus:outline-none focus:ring-2 transition-all border ${theme.colors.input.bg} ${theme.colors.input.border} ${theme.colors.input.text} ${theme.colors.input.focusRing}`}
                            />
                        </div>

                        <button 
                            onClick={handleDebate}
                            disabled={!optionA || !optionB}
                            className={`w-full py-4 font-bold rounded-xl transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4 ${theme.components.button.primary}`}
                        >
                            Start Debate <ArrowRight size={20} />
                        </button>
                    </div>
                </div>
            )}

            {step === 'loading' && (
                <div className="flex flex-col items-center justify-center h-full py-20">
                    <div className="relative w-24 h-24 mb-8">
                        <div className={`absolute inset-0 border-4 rounded-full ${theme.colors.border}`}></div>
                        <div className={`absolute inset-0 border-4 rounded-full border-t-transparent animate-spin ${theme.mode === 'dark' ? 'border-indigo-400' : 'border-indigo-600'}`}></div>
                        <Scale className={`absolute inset-0 m-auto ${theme.colors.primary}`} size={32} />
                    </div>
                    <h3 className={`text-xl font-bold mb-2 ${theme.colors.text.main}`}>Simulating Internal Conflict...</h3>
                    <p className={`animate-pulse ${theme.colors.text.secondary}`}>Summoning the Pragmatist and the Visionary.</p>
                </div>
            )}

            {step === 'results' && result && (
                <div className="space-y-8 animate-scale-up p-6">
                    
                    {/* The Debate Stage */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* The Pragmatist */}
                        <div className={`rounded-2xl p-6 border relative overflow-hidden group hover:shadow-md transition-all ${theme.mode === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <Brain size={120} />
                            </div>
                            <div className="relative z-10">
                                <div className={`flex items-center gap-3 mb-4 ${theme.mode === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
                                    <Brain size={24} />
                                    <h3 className="font-bold text-lg">The Pragmatist</h3>
                                </div>
                                <div className={`leading-relaxed text-sm p-4 rounded-xl border shadow-sm ${theme.mode === 'dark' ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-slate-100 text-slate-600'}`}>
                                    "{result.pragmatistArgument}"
                                </div>
                            </div>
                        </div>

                        {/* The Visionary */}
                        <div className={`rounded-2xl p-6 border relative overflow-hidden group hover:shadow-md transition-all ${theme.mode === 'dark' ? 'bg-rose-900/20 border-rose-900/30' : 'bg-rose-50 border-rose-100'}`}>
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <Heart size={120} />
                            </div>
                            <div className="relative z-10">
                                <div className={`flex items-center gap-3 mb-4 ${theme.mode === 'dark' ? 'text-rose-300' : 'text-rose-700'}`}>
                                    <Heart size={24} />
                                    <h3 className="font-bold text-lg">The Visionary</h3>
                                </div>
                                <div className={`leading-relaxed text-sm p-4 rounded-xl border shadow-sm ${theme.mode === 'dark' ? 'bg-slate-900 border-rose-900/50 text-rose-100' : 'bg-white border-rose-100 text-rose-800'}`}>
                                    "{result.visionaryArgument}"
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Synthesis & Blindspot */}
                    <div className={`rounded-2xl p-6 border flex flex-col md:flex-row gap-8 ${theme.mode === 'dark' ? 'bg-indigo-900/20 border-indigo-900/30' : 'bg-indigo-50 border-indigo-100'}`}>
                         <div className="flex-1">
                             <h4 className={`flex items-center gap-2 font-bold mb-3 ${theme.colors.primary}`}>
                                <AlertTriangle size={18} /> The Blind Spot
                             </h4>
                             <p className={`text-sm leading-relaxed ${theme.colors.text.main}`}>
                                {result.blindSpot}
                             </p>
                         </div>
                         <div className={`w-px hidden md:block ${theme.mode === 'dark' ? 'bg-indigo-900' : 'bg-indigo-200'}`}></div>
                         <div className="flex-1">
                             <h4 className={`flex items-center gap-2 font-bold mb-3 ${theme.colors.primary}`}>
                                <Sparkles size={18} /> Kai's Synthesis
                             </h4>
                             <p className={`text-sm leading-relaxed ${theme.colors.text.main}`}>
                                {result.synthesis}
                             </p>
                         </div>
                    </div>

                    {/* Verdict */}
                    <div className="flex justify-center">
                        <div className="inline-flex items-center gap-2 px-6 py-2 bg-gray-900 text-white rounded-full text-sm font-bold shadow-lg">
                            <CheckCircle size={16} className="text-green-400" />
                            Current Leaning: {result.winner}
                        </div>
                    </div>

                    <div className={`flex justify-center pt-8 border-t ${theme.colors.divider}`}>
                        <button 
                            onClick={() => {
                                setStep('input');
                                setOptionA('');
                                setOptionB('');
                                setResult(null);
                            }}
                            className={`${theme.colors.text.secondary} hover:text-indigo-500 text-sm font-medium transition-colors`}
                        >
                            Start New Debate
                        </button>
                    </div>
                </div>
            )}
    </BaseModal>
  );
};
